import { useState, useEffect, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { setSelectedManagement } from "../../../../../redux/mgmtCo/selectedManagementSlice";
import { clearSelectedCustomer } from "../../../../../redux/mgmtCo/selectedCustomerSlice";
import { clearSelectedContact } from "../../../../../redux/mgmtCo/selectedContactSlice";
import useViewManagementCompany from "./useViewManagementCompany";
import useDeleteManagementCompany from "./useDeleteManagementCompany";
import useDebounce from "../../../../../hooks/useDebounce";
import { selectNavHeight } from "../../../../../redux/navbar/navHeightSlice";

const useViewModalLogic = (updateList, handleClose) => {
  const dispatch = useDispatch();
  const companies = useSelector((state) => state.management.companies);
  const navHeight = useSelector(selectNavHeight);

  // Hook handling form editing and autosave logic.
  const {
    isEditMode,
    setIsEditMode,
    formData,
    handleFieldChange,
    handleSave,
    handleToggleEdit,
    validationErrors,
    isSubmitting,
    clearValidation,
  } = useViewManagementCompany(null, updateList);

  const deleteSelected = useDeleteManagementCompany();

  // State for info display toggle.
  const [isInfoOpen, setIsInfoOpen] = useState(false);
  const handleInfoClick = () => {
    setIsInfoOpen((prev) => !prev);
  };

  // New entity state for when the user wants to add a new contact/customer.
  const [newEntityMode, setNewEntityMode] = useState({
    active: false,
    type: null,
  });
  const [newCustomerId, setNewCustomerId] = useState("");

  // New state for showing the new contact form.
  const [showNewContactForm, setShowNewContactForm] = useState(false);

  // Autosave state using a debounced version of the form data.
  const [autosaveStatus, setAutosaveStatus] = useState("");
  const debouncedFormData = useDebounce(formData, 1500);
  useEffect(() => {
    if (isEditMode) {
      setAutosaveStatus("Saving...");
      handleSave(true).then((success) => {
        if (success) {
          setAutosaveStatus("Saved");
          setTimeout(() => setAutosaveStatus(""), 2000);
        } else {
          setAutosaveStatus("");
        }
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedFormData]);

  // Expanded state for the modal’s layout.
  const [isExpanded, setIsExpanded] = useState(false);

  // Snackbar state and handler.
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "info",
  });
  const handleSnackbarClose = (event, reason) => {
    if (reason === "clickaway") return;
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  // Clear selected customer/contact when navigating.
  const clearSelected = useCallback(() => {
    setIsInfoOpen(false);
    dispatch(clearSelectedCustomer());
    dispatch(clearSelectedContact());
  }, [dispatch]);

  const handlePrev = useCallback(() => {
    if (!companies || companies.length === 0) return;
    const currentIndex = companies.findIndex(
      (comp) => comp.managementId === formData.managementId
    );
    const prevIndex = (currentIndex - 1 + companies.length) % companies.length;
    dispatch(setSelectedManagement(companies[prevIndex]));
    clearSelected();
  }, [companies, formData.managementId, dispatch, clearSelected]);

  const handleNext = useCallback(() => {
    if (!companies || companies.length === 0) return;
    const currentIndex = companies.findIndex(
      (comp) => comp.managementId === formData.managementId
    );
    const nextIndex = (currentIndex + 1) % companies.length;
    dispatch(setSelectedManagement(companies[nextIndex]));
    clearSelected();
  }, [companies, formData.managementId, dispatch, clearSelected]);

  const handleDelete = async () => {
    try {
      await deleteSelected();
      setSnackbar({
        open: true,
        message: "Management company deleted successfully.",
        severity: "success",
      });
      handleClose();
    } catch (error) {
      setSnackbar({ open: true, message: error.message, severity: "error" });
    }
  };

  const handleCloseModal = () => {
    setIsEditMode(false);
    clearValidation();
    clearSelected();
    handleClose();
  };

  // Local state for managing a contact form within the modal.
  const [contactForm, setContactForm] = useState({});
  const handleContactChange = (e) => {
    const { name, value } = e.target;
    setContactForm((prev) => ({ ...prev, [name]: value }));
  };

  // When a user clicks "New" in either Contacts or Customers table.
  const handleOpenNew = (entityType) => {
    if (entityType === "contact") {
      dispatch(clearSelectedContact());
    } else if (entityType === "customer") {
      dispatch(clearSelectedCustomer());
    }
    setNewEntityMode({ active: true, type: entityType });
    setIsInfoOpen(true);
  };

  return {
    // From useViewManagementCompany:
    isEditMode,
    setIsEditMode,
    formData,
    handleFieldChange,
    handleSave,
    handleToggleEdit,
    validationErrors,
    isSubmitting,
    clearValidation,
    // Additional modal state/handlers:
    navHeight,
    isInfoOpen,
    handleInfoClick,
    newEntityMode,
    setNewEntityMode,
    newCustomerId,
    setNewCustomerId,
    showNewContactForm,
    setShowNewContactForm,
    autosaveStatus,
    isExpanded,
    setIsExpanded,
    snackbar,
    handleSnackbarClose,
    clearSelected,
    handleDelete,
    handleCloseModal,
    handlePrev,
    handleNext,
    contactForm,
    handleContactChange,
    handleOpenNew,
  };
};

export default useViewModalLogic;
